import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

interface Props {
  categorySlug: string;
}

export function WikiCategoryPages({ categorySlug }: Props) {
  const categoriesQuery = useQuery({
    queryKey: ['wiki', 'categories'],
    queryFn: api.wikiCategories,
    staleTime: 60_000,
  });
  const pagesQuery = useQuery({
    queryKey: ['wiki', 'pages', { category: categorySlug }],
    queryFn: () => api.wikiPages({ category: categorySlug, limit: 100 }),
  });

  const category = categoriesQuery.data?.categories.find((c) => c.slug === categorySlug);

  if (pagesQuery.isLoading) {
    return <div className="p-8 text-foreground-muted text-sm">로딩 중…</div>;
  }
  if (pagesQuery.isError) {
    return (
      <div className="p-8 text-destructive text-sm">
        페이지를 불러오지 못했습니다: {(pagesQuery.error as Error).message}
      </div>
    );
  }

  const pages = pagesQuery.data?.pages ?? [];

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-6">
        <Link
          to="/wikis"
          className="mb-2 inline-block text-xs text-foreground-muted hover:text-accent"
        >
          ← 위키 목차
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">{category?.name ?? categorySlug}</h1>
        {category?.description ? (
          <p className="mt-1 text-foreground-muted">{category.description}</p>
        ) : null}
        <p className="mt-2 text-sm text-foreground-faint">{pages.length}개 페이지</p>
      </header>

      {pages.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-6 text-center text-foreground-muted">
          이 카테고리에는 아직 페이지가 없습니다.
        </div>
      ) : (
        <ul className="space-y-2">
          {pages.map((p) => (
            <li
              key={p.slug}
              className="rounded-md border border-border bg-surface p-3 hover:border-accent transition-colors"
            >
              <Link to="/wikis/p/$slug" params={{ slug: p.slug }} className="block">
                <div className="font-medium">{p.title}</div>
                <div className="mt-1 text-sm text-foreground-muted line-clamp-2">{p.takeaway}</div>
                <div className="mt-1 text-xs text-foreground-faint">
                  <code className="rounded bg-code-bg px-1 py-0.5">{p.slug}</code>
                  <span className="ml-2">{new Date(p.updatedAt).toLocaleDateString('ko-KR')}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
